import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { SearchInput } from 'src/components/atoms';
import { Table } from 'src/components/molecules/Table';
import type { ColumnType } from 'src/components/molecules/TableRow';
import { UserType } from 'src/graphql';
import type { SearchUsersPaginatedQuery } from 'src/graphql/generated';
import { appointCaseManagerModalMessages } from './appointCaseManagerModalMessages';
import {
  DirectoryRoot,
  DirectoryTableWrap,
  SecondaryCellText,
} from './CaseManagerDirectoryPicker.styles';

const TABLE_COLUMNS: ColumnType[] = [
  { label: 'Name' },
  { label: 'Department' },
  { label: 'E-mail address' },
];

export type CaseManagerDirectoryUser =
  SearchUsersPaginatedQuery['searchUsersPaginated']['items'][number];

interface Props {
  sortedValidUsers: CaseManagerDirectoryUser[];
  selectedUser: UserType | null;
  setSelectedUser: Dispatch<SetStateAction<UserType | null>>;
  searchUsers: (term: string) => void;
  placeholder: string;
  visible: boolean;
  testIdPrefix: string;
}

export const CaseManagerDirectoryPicker = ({
  sortedValidUsers,
  selectedUser,
  setSelectedUser,
  searchUsers,
  placeholder,
  visible,
  testIdPrefix,
}: Props) => {
  const [searchFieldValue, setSearchFieldValue] = useState('');

  useEffect(() => {
    if (visible) {
      setSearchFieldValue('');
      searchUsers('');
    }
  }, [visible, searchUsers]);

  const handleRowActivate = useCallback(
    (user: CaseManagerDirectoryUser) => {
      if (user.eid === selectedUser?.eid) {
        setSelectedUser(null);
      } else {
        setSelectedUser(user as UserType);
      }
    },
    [selectedUser?.eid, setSelectedUser],
  );

  const tableData = useMemo(() => {
    return sortedValidUsers.map((user) => {
      const rowKey = String(user.eid || user.id);

      return {
        row: rowKey,
        data: user,
        cellData: [
          user.name || 'Anonymous',
          <SecondaryCellText key={`dept-${rowKey}`}>
            {user.department || '—'}
          </SecondaryCellText>,
          <SecondaryCellText key={`email-${rowKey}`}>
            {user.email || '—'}
          </SecondaryCellText>,
        ],
      };
    });
  }, [sortedValidUsers]);

  const selectedRows = useMemo(() => {
    if (!selectedUser?.eid) {
      return [];
    }
    const match = sortedValidUsers.find((u) => u.eid === selectedUser.eid);
    return match ? [String(match.eid || match.id)] : [];
  }, [selectedUser?.eid, sortedValidUsers]);

  return (
    <DirectoryRoot data-testid={`${testIdPrefix}-directory`}>
      <SearchInput
        hideLabel
        value={searchFieldValue}
        onChange={(e) => {
          const value = e.target.value;
          setSearchFieldValue(value);
          searchUsers(value);
        }}
        placeholder={placeholder}
        data-testid="search-bar-input/case-manager-directory"
        aria-label={placeholder}
        $width="100%"
      />
      <DirectoryTableWrap>
        <Table<CaseManagerDirectoryUser>
          data-testid="search-bar"
          columns={TABLE_COLUMNS}
          tableData={tableData}
          emptyText={appointCaseManagerModalMessages.directoryTableEmpty}
          allowRowSelect
          selectionVariant="radio"
          selectionGroupName={`${testIdPrefix}-case-manager`}
          selectedRows={selectedRows}
          isFullWidth
          small
          hasStickyHeader={false}
          onRowClick={(_row, data, _e) => {
            if (!data) {
              return;
            }
            handleRowActivate(data);
          }}
        />
      </DirectoryTableWrap>
    </DirectoryRoot>
  );
};
